import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import { ReactComponent as IconFilter } from "@common_assets/svg/filters.svg"
import { ReactComponent as IconTrash } from "@common_assets/svg/trash-circular.svg"
import {
  Stack,
  useTheme,
  Fab,
  Box,
  Popover,
  Grid,
  Button,
  Checkbox,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material"
import { useMutationSchoolTransactions } from "api/reactQuery/mutations/adminTeacher"
import { ExportType } from "api/reactQuery/queries/export.types"
import {
  useInfiniteTeacherAdminSchoolTransactions,
  useQuerySchoolTransactionsIds,
  useQueryExportAdminTransactions,
  useQuerySchoolTransactionsSchoolData,
} from "api/reactQuery/queries/teacherAdmin"
import DeleteTransactionsDialog, {
} from "components/common/deleteTransactionsDialog"
import DialogUpgradeAccount from "components/common/dialogUpgradeAccount/DialogUpgradeAccount"
import {
  ExportButton,
  ExportConfirmDialog,
  ExportMenu,
  ExportPendingBanner,
} from "components/common/exportMenu"
import Coin from "components/common/icon/coin"
import LoadingIndicator from "components/common/loadingIndicator"
import BasicTable from "components/common/table/basicTable"
import TransactionsSelectionBar from "components/common/transactionsSelectionBar"
import TransactionsTableArea from "components/common/transactionsTableArea"
import TransactionType from "components/common/transactionType"
import { getSignByTransactionType } from "components/module/studentProfile/transactions/transactions.utils"
import dayjs from "dayjs"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useTransactionsDeleteFlow } from "hooks/deleteTransactionsTask"
import { useDialog } from "hooks/dialog"
import {
  useExportGuard,
  useExportMenu,
  useExportTasks,
} from "hooks/exportReports"
import { useCustomPayment } from "hooks/payment"
import { usePopover } from "hooks/popover"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import { useTransactionSelection } from "hooks/transactionSelection"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import {
  selectClassesId,
  selectHelpersId,
  selectSender,
  selectTeachersId,
  selectTitle,
  selectTransactionsType,
} from "store/adminTransaction/adminTransactions.selectors"
import {
  resetFilters,
  setClickedReset,
  setTransactionsType,
} from "store/adminTransaction/adminTransactions.slice"
import {
  SenderType,
  TypeIn,
} from "store/adminTransaction/adminTransactions.types"
import { selectUser } from "store/auth/auth.selectors"
import { unstyledButtonSx } from "styles/common/button"
import { visuallyHiddenSx } from "styles/common/typography"
import { getErrorMessageFromTab } from "utils/api"
import { schoolPlanToExportData } from "utils/export"
import { isSameQueryParams } from "utils/reactQuery"

import Autocomplete from "./autocomplete"
import DatePicker from "./datePicker"
import ModalContentFilters from "./modalContentFilters"
import { StudentProps } from "./SchoolTransactions.types"

interface DateRangeProps {
  from: dayjs.Dayjs | undefined
  to: dayjs.Dayjs | undefined
}

interface ShowSelectedOptions {
  title: string
  id: number | string
}

const formatDate = (date: dayjs.Dayjs | undefined) =>
  date !== undefined && date.isValid() ? date.format("YYYY-MM-DD") : undefined

const EMPTY_DATE_RANGE: DateRangeProps = { from: undefined, to: undefined }

interface IAppliedFilters {
  students: StudentProps[]
  dateRange: DateRangeProps
  classes: number[]
  teachers: number[]
  studentHelpers: number[]
  transactionType: TypeIn[]
  title: string
  sender: SenderType
}

const EMPTY_APPLIED_FILTERS: IAppliedFilters = {
  students: [],
  dateRange: EMPTY_DATE_RANGE,
  classes: [],
  teachers: [],
  studentHelpers: [],
  transactionType: [],
  title: "",
  sender: "",
}

const toQueryParams = (applied: IAppliedFilters) => ({
  classes: applied.classes,
  teachers: applied.teachers,
  students: applied.students.map((student) => student.id),
  type_in: applied.transactionType,
  title: applied.title.length ? applied.title : undefined,
  student_helpers: applied.studentHelpers,
  sender_type: applied.sender.length ? applied.sender : undefined,
  date_from: formatDate(applied.dateRange.from),
  date_to:
    applied.dateRange.from && !applied.dateRange.to
      ? formatDate(dayjs())
      : formatDate(applied.dateRange.to),
})

const SchoolTransactions = () => {
  const theme = useTheme()
  const isDesktop = useIsBreakpointUp("desktop")
  const isTablet = useIsBreakpointUp("tablet")
  const { showSnackbar } = useCustomSnackbar()
  const { t } = useTranslation()
  const { ref, inView } = useInView({ threshold: 0.5 })
  const userInfo = useAppSelector(selectUser)
  const dispatch = useAppDispatch()

  const classes = useAppSelector(selectClassesId)
  const teachers = useAppSelector(selectTeachersId)
  const transactionType = useAppSelector(selectTransactionsType)
  const title = useAppSelector(selectTitle)
  const studentHelpers = useAppSelector(selectHelpersId)
  const sender = useAppSelector(selectSender)

  const [showSelectedOptions, setShowSelectedOptions] = useState<
    ShowSelectedOptions[]
  >([])
  const [isExportData, setIsExportData] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<StudentProps[]>([])
  const [dateRange, setDateRange] = useState<DateRangeProps>(EMPTY_DATE_RANGE)
  const [applied, setApplied] = useState<IAppliedFilters>(
    EMPTY_APPLIED_FILTERS,
  )
  const [isDateError, setIsDateError] = useState(false)
  const { payIt, isLoadingPayments } = useCustomPayment()
  const { popoverAnchorEl, handleClose, handleClick, setPopoverAnchorEl } =
    usePopover<HTMLButtonElement>()

  const [mobileActionsAnchor, setMobileActionsAnchor] =
    useState<null | HTMLElement>(null)

  const handleMobileActionsOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setMobileActionsAnchor(event.currentTarget)
  }

  const handleMobileActionsClose = () => {
    setMobileActionsAnchor(null)
  }
  const {
    isOpen: isOpenExport,
    handleOpen: handleOpenExport,
    handleClose: handleCloseExport,
  } = useDialog()

  const {
    successTasks,
    pendingTasks,
    refreshTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
  } = useExportTasks({ exportType: ExportType.SCHOOL_TRANSACTIONS })

  const { data: schoolData, isLoading: isLoadingSchoolData } =
    useQuerySchoolTransactionsSchoolData({ options: {} })

  const appliedIds = useMemo(() => {
    const ids = new Set<number | string>()
    applied.students.forEach((s) => ids.add(s.id))
    if (applied.dateRange.from) ids.add("from")
    if (applied.dateRange.to) ids.add("to")
    applied.transactionType.forEach((t) => ids.add(t))
    return ids
  }, [applied])

  const sharedQueryParams = useMemo(() => toQueryParams(applied), [applied])

  const commitFilters = (next: IAppliedFilters) => {
    if (isSameQueryParams(toQueryParams(next), sharedQueryParams)) return

    setApplied(next)
    selection.clear()
  }

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useInfiniteTeacherAdminSchoolTransactions({
      ...sharedQueryParams,
      limit: 20,
      skip: 0,
      options: {
        enabled: !!schoolData,
        onError: (err) => {
          showSnackbar({ title: getErrorMessageFromTab(err), variant: "error" })
        },
      },
    })

  const selectableIds = useMemo(
    () => data?.pages.flatMap((page) => page.data.map(({ id }) => id)) ?? [],
    [data],
  )
  const selection = useTransactionSelection({
    selectableIds,
    useMatchingIds: (enabled) =>
      useQuerySchoolTransactionsIds({
        params: sharedQueryParams,
        options: { enabled },
      }),
  })

  const {
    isGenerateDisabled,
    isConfirmDialogOpen,
    handleCloseConfirmDialog,
    handleConfirm: handleConfirmExport,
    handleGenerate: guardGenerate,
    onExportTriggered,
  } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
    queryParams: sharedQueryParams,
  })

  const { isFetching, refetch: refetchExportData } =
    useQueryExportAdminTransactions({
      ...sharedQueryParams,
      options: {
        enabled: isExportData,
        onSuccess: (response) => {
          setIsExportData(false)
          handleExportSuccess(response)
          onExportTriggered()
          showSnackbar({ title: t("export.fileIsCurrentlyBeingGenerated") })
        },
        onError: (err) => {
          showSnackbar({ title: getErrorMessageFromTab(err), variant: "error" })
        },
      },
    })

  const {
    exportMenuAnchorEl,
    isExportMenuOpen,
    handleExportClick,
    handleExportMenuClose,
    handleGenerateNewFile: handleGenerateFile,
    openExportMenu,
  } = useExportMenu(isFetching, refreshTasks)

  const triggerExport = useCallback(() => {
    schoolPlanToExportData({
      user: userInfo,
      upgrade: handleOpenExport,
      exportData: refetchExportData,
    })
  }, [userInfo, handleOpenExport, refetchExportData])

  const handleGenerateWithConfirmation = useCallback(() => {
    handleGenerateFile(() => {
      guardGenerate(triggerExport)
    })
  }, [handleGenerateFile, guardGenerate, triggerExport])

  const deleteFlow = useTransactionsDeleteFlow({
    view: "schoolAdmin",
    scopeId:
      userInfo && "school_id" in userInfo.user
        ? userInfo.user.school_id
        : undefined,
    onDeleted: selection.clear,
  })
  const { activeTask } = deleteFlow

  const { mutate, isLoading: isDeleting } = useMutationSchoolTransactions({
    options: deleteFlow.mutationOptions,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !activeTask) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, activeTask])

  useEffect(() => {
    const mappedDate = Object.entries(dateRange)
      .filter(([, date]) => date !== undefined && dayjs(date).isValid())
      .map(([key, value]) => ({
        title: `${key}: ${dayjs(value).format("MM-DD-YYYY")}`,
        id: key,
      }))

    const mappedSelectionOptions = selectedOptions.map((item) => ({
      title: `${item.first_name} ${item.last_name}`,
      id: item.id,
    }))

    const mappedTransactionsType = transactionType.map((item) => ({
      title: item,
      id: item,
    }))

    setShowSelectedOptions([
      ...mappedSelectionOptions,
      ...mappedDate,
      ...mappedTransactionsType,
    ])
  }, [selectedOptions, dateRange, transactionType])

  const draftFilters: IAppliedFilters = {
    students: selectedOptions,
    dateRange,
    classes,
    teachers,
    studentHelpers,
    transactionType,
    title,
    sender,
  }

  const handleResetFilters = () => {
    setSelectedOptions([])
    setDateRange(EMPTY_DATE_RANGE)
    setIsDateError(false)
    commitFilters(EMPTY_APPLIED_FILTERS)
    dispatch(resetFilters())
    dispatch(setClickedReset(true))
    setTimeout(() => {
      dispatch(setClickedReset(false))
    }, 0)
  }

  const handleApply = () => {
    commitFilters(draftFilters)
  }

  // The drafts match what the list shows: chips render as applied and Apply
  // has nothing to do.
  const isFiltersApplied = isSameQueryParams(
    toQueryParams(draftFilters),
    sharedQueryParams,
  )

  const handleSelectedOptions = useCallback((options: StudentProps[]) => {
    setSelectedOptions(options)
  }, [])

  const hasAnyFilter =
    !!dateRange.from ||
    !!dateRange.to ||
    selectedOptions.length > 0 ||
    transactionType.length > 0 ||
    classes.length > 0 ||
    teachers.length > 0 ||
    studentHelpers.length > 0 ||
    title.length > 0 ||
    sender.length > 0
  const isApplyDisabled = isDateError || !hasAnyFilter || isFiltersApplied

  const datePickerProps = {
    dateRange,
    setDateRange,
    onErrorChange: setIsDateError,
  }

  const handleClickOut = (id: number | string) => {
    if (typeof id === "string") {
      if (id === "from" || id === "to") {
        setDateRange({ ...dateRange, [id]: undefined })
        // The chip shows the draft date; an applied date that differs from it
        // isn't what the user removed.
        if (formatDate(dateRange[id]) === formatDate(applied.dateRange[id])) {
          commitFilters({
            ...applied,
            dateRange: { ...applied.dateRange, [id]: undefined },
          })
        }
      } else {
        dispatch(
          setTransactionsType(
            transactionType.filter((idType) => idType !== id),
          ),
        )
        commitFilters({
          ...applied,
          transactionType: applied.transactionType.filter(
            (idType) => idType !== id,
          ),
        })
      }
    }
    if (typeof id === "number") {
      setSelectedOptions(selectedOptions.filter((item) => item.id !== id))
      commitFilters({
        ...applied,
        students: applied.students.filter((item) => item.id !== id),
      })
    }
  }

  const autocompleteProps = {
    options: schoolData?.data.students ?? [],
    selectedOptions,
    handleSelected: handleSelectedOptions,
    disabled: isDateError,
  }

  return (
    <Stack
      p={2}
      sx={{
        backgroundColor: theme.palette.common.white,
        borderRadius: "4px 4px 0 0 ",
      }}
    >
      <Typography variant="h1" sx={visuallyHiddenSx}>
        {t("adminDashboard.schoolTransactions")}
      </Typography>
      {isDesktop ? (
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          sx={{ marginBottom: 3, flexWrap: "nowrap" }}
        >
          <Grid sx={{ flex: "1 1 0", minWidth: 0, alignSelf: "center" }}>
            <Autocomplete {...autocompleteProps} />
          </Grid>
          <Grid sx={{ flex: "1 1 0", minWidth: 0 }}>
            <DatePicker {...datePickerProps} />
          </Grid>
          <Grid sx={{ flexShrink: 0, alignSelf: "center" }}>
            <Button disabled={isApplyDisabled} onClick={handleApply}>
              {t("allTransactions.apply")}
            </Button>
          </Grid>
          <Grid sx={{ flexShrink: 0, alignSelf: "center" }}>
            <Box display="flex" justifyContent="flex-start" columnGap={1}>
              <Fab onClick={handleClick}>
                <IconFilter />
              </Fab>
              <ExportButton
                isFetching={isFetching}
                onClick={handleExportClick}
              />
            </Box>
          </Grid>
        </Grid>
      ) : isTablet ? (
        <>
          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ marginBottom: 2, flexWrap: "nowrap" }}
          >
            <Grid sx={{ flex: "1 1 0", minWidth: 0 }}>
              <Autocomplete {...autocompleteProps} />
            </Grid>
            <Grid sx={{ flexShrink: 0 }}>
              <Box display="flex" columnGap={1} alignItems="center">
                <Fab onClick={handleClick}>
                  <IconFilter />
                </Fab>
                <ExportButton
                  isFetching={isFetching}
                  onClick={handleExportClick}
                />
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ marginBottom: 2 }}>
            <DatePicker {...datePickerProps} />
          </Box>
          <Box mb={3}>
            <Button disabled={isApplyDisabled} onClick={handleApply} fullWidth>
              {t("allTransactions.apply")}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ marginBottom: 2 }}>
            <Autocomplete {...autocompleteProps} />
          </Box>
          <Box sx={{ marginBottom: 2 }}>
            <Button
              variant="outlined"
              endIcon={<IconChevronDown />}
              onClick={handleMobileActionsOpen}
              fullWidth
            >
              {t("adminDashboard.actions")}
            </Button>
            <Menu
              anchorEl={mobileActionsAnchor}
              open={Boolean(mobileActionsAnchor)}
              onClose={handleMobileActionsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: { sx: { width: mobileActionsAnchor?.offsetWidth } },
              }}
            >
              <MenuItem
                onClick={() => {
                  setPopoverAnchorEl(mobileActionsAnchor as HTMLButtonElement)
                  handleMobileActionsClose()
                }}
              >
                {t("adminDashboard.filters")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (mobileActionsAnchor) openExportMenu(mobileActionsAnchor)
                  handleMobileActionsClose()
                }}
              >
                {t("adminDashboard.export")}
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ marginBottom: 2 }}>
            <DatePicker {...datePickerProps} />
          </Box>
          <Box mb={3}>
            <Button disabled={isApplyDisabled} onClick={handleApply} fullWidth>
              {t("allTransactions.apply")}
            </Button>
          </Box>
        </>
      )}
      <DialogUpgradeAccount
        open={isOpenExport}
        onClose={handleCloseExport}
        onActionButtonClick={() => payIt(null)}
        isLoading={isLoadingPayments}
        isExport
      />
      <ExportMenu
        anchorEl={exportMenuAnchorEl}
        open={isExportMenuOpen}
        onClose={handleExportMenuClose}
        onGenerateNewFile={handleGenerateWithConfirmation}
        onFileClick={setExportReportIdToDownload}
        successTasks={successTasks}
        isGenerateDisabled={isGenerateDisabled}
        generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
      />
      <Popover
        anchorEl={popoverAnchorEl}
        open={!!popoverAnchorEl}
        onClose={handleClose}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: "700px", marginTop: "5px" } } }}
      >
        <ModalContentFilters />
        <Box textAlign="end" mr={2} mb={2}>
          <Button variant="text" onClick={handleResetFilters}>
            {t("allTransactions.resetFilters")}
          </Button>
          <Button
            disabled={isApplyDisabled}
            onClick={() => {
              handleApply()
              handleClose()
            }}
          >
            {t("allTransactions.apply")}
          </Button>
        </Box>
      </Popover>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
        {showSelectedOptions.map((item) => {
          const isApplied = isFiltersApplied && appliedIds.has(item.id)
          return (
            <Stack
              key={item.id}
              component="button"
              type="button"
              aria-label={t("adminDashboard.removeSelectedFilter", {
                title: item.title,
              })}
              sx={{
                px: 1,
                py: 0.5,
                flexDirection: "row",
                columnGap: 1,
                mb: 1,
                width: "fit-content",
                borderRadius: 2,
                alignItems: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                margin: 0,
                ...(isApplied
                  ? {
                      backgroundColor: theme.palette.mockup.neutral90,
                      border: "1px solid transparent",
                    }
                  : {
                      backgroundColor: "transparent",
                      border: `1px dashed ${theme.palette.mockup.primary60}`,
                      opacity: 0.75,
                    }),
              }}
              onClick={() => handleClickOut(item.id)}
            >
              <Typography
                component="span"
                variant="body3"
                sx={{
                  color: isApplied ? "inherit" : theme.palette.mockup.primary60,
                }}
              >
                {item.title.toUpperCase()}
              </Typography>
              <Box
                component="span"
                fontSize="14px"
                sx={{
                  color: isApplied ? "inherit" : theme.palette.mockup.primary60,
                }}
              >
                x
              </Box>
            </Stack>
          )
        })}
        {!!showSelectedOptions.length && (
          <Button
            onClick={handleResetFilters}
            variant="text"
            sx={{
              color: theme.palette.mockup.primary60,
              height: "28px",
              fontSize: "12px",
              padding: "0 6px",
            }}
          >
            {t("allTransactions.resetFilters")}
          </Button>
        )}
      </Stack>
      <TransactionsTableArea deletingCount={activeTask?.count ?? null}>
        <BasicTable
          topSlot={
            <TransactionsSelectionBar
              {...selection.bar}
              onDeleteSelected={() => deleteFlow.openBatch(selection.selection)}
            />
          }
          head={
            <BasicTable.Row>
              <BasicTable.Cell>
                <Checkbox
                  onChange={selection.header.onToggle}
                  checked={selection.header.isChecked}
                  indeterminate={selection.header.isIndeterminate}
                  disabled={selection.header.isDisabled}
                  slotProps={{
                    input: {
                      "aria-label": t("allTransactions.selectAllTransactions"),
                    },
                  }}
                />
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.type")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.classAndTeachers")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.student")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.title")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.date")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell>
                <BasicTable.HeadText>
                  {t("adminDashboard.amount")}
                </BasicTable.HeadText>
              </BasicTable.Cell>
              <BasicTable.Cell />
            </BasicTable.Row>
          }
          body={data?.pages.flatMap((data) =>
            data.data.map((transaction) => (
              <BasicTable.Row key={transaction.id}>
                <BasicTable.Cell>
                  <Checkbox
                    checked={selection.isRowChecked(transaction.id)}
                    onChange={() => selection.toggleRow(transaction.id)}
                    slotProps={{
                      input: {
                        "aria-label": t("allTransactions.selectTransaction", {
                          title: transaction.title,
                          student: `${transaction.student.first_name} ${transaction.student.last_name}`,
                        }),
                      },
                    }}
                  />
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <TransactionType transaction={transaction} />
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Stack>
                    <Typography fontWeight="500" variant="body2">
                      {transaction.class_name}
                    </Typography>
                    <Typography fontWeight="500" variant="body2">
                      {transaction.teacher?.last_name}
                    </Typography>
                  </Stack>
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Typography fontWeight="500" variant="body2">
                    {transaction.student.first_name}{" "}
                    {transaction.student.last_name}
                  </Typography>
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Typography fontWeight="500" variant="body2">
                    {transaction.title}
                  </Typography>
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Typography fontWeight="500" variant="body2">
                    {transaction?.created_at &&
                      dayjs(transaction.created_at).format("MMM DD, YYYY")}
                  </Typography>
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Coin
                    size="small"
                    amount={Math.abs(transaction.amount)}
                    sign={
                      transaction.amount !== 0
                        ? transaction.amount > 0
                          ? "+"
                          : "-"
                        : getSignByTransactionType(transaction.type)
                    }
                  />
                </BasicTable.Cell>
                <BasicTable.Cell>
                  <Box
                    component="button"
                    type="button"
                    aria-label={t("adminDashboard.deleteTransaction")}
                    onClick={() =>
                      deleteFlow.openSingle(transaction.id, transaction.title)
                    }
                    sx={{
                      ...unstyledButtonSx,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: `1.5px solid ${theme.palette.primary.main}`,
                      color: theme.palette.primary.main,
                      ":hover": {
                        transition: "background .5s",
                        background: theme.palette.mockup.primary95,
                      },
                      ":focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <IconTrash />
                  </Box>
                </BasicTable.Cell>
              </BasicTable.Row>
            )),
          )}
          bottomSlot={
            <>
              {(isLoading || isLoadingSchoolData) && (
                <Box
                  component="span"
                  sx={{ display: "block", textAlign: "center", marginTop: 2 }}
                >
                  <LoadingIndicator />
                </Box>
              )}
              {!isLoading &&
                !isLoadingSchoolData &&
                !data?.pages?.flatMap((page) => page.data)?.length && (
                  <Typography m={2}>
                    {t("adminDashboard.noResultsMatchingFilters")}
                  </Typography>
                )}
              {hasNextPage && <div ref={ref} />}
            </>
          }
        />
      </TransactionsTableArea>
      <DeleteTransactionsDialog
        {...deleteFlow.dialog}
        isPending={isDeleting}
        onConfirm={() =>
          deleteFlow.request &&
          mutate({ transaction_ids: deleteFlow.request.ids })
        }
      />
      <ExportConfirmDialog
        open={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmExport}
      />
      <ExportPendingBanner visible={pendingTasks.length > 0} />
    </Stack>
  )
}
export default SchoolTransactions
